import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Eye, Heart, MessageSquare, Share2, Users, TrendingUp } from 'lucide-react';
import { MetricCard, PerformanceChart, FollowerGrowth } from '../components';
import { useAnalyticsOverview, useAnalyticsPerformance, useAnalyticsAudience } from '../hooks';

const METRICS = ['VIEWS', 'LIKES', 'COMMENTS', 'SHARES', 'FOLLOWERS'] as const;

export function DashboardPage() {
  const { t } = useTranslation();
  const [days, setDays] = useState(7);
  const [selectedMetric, setSelectedMetric] = useState<string>('VIEWS');

  const { data: overview, isLoading } = useAnalyticsOverview(days);
  const { data: performance } = useAnalyticsPerformance(days, selectedMetric);
  const { data: audience } = useAnalyticsAudience();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-neutral-500">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6 px-4 sm:px-6 md:px-8"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-semibold tracking-wide">{t('dashboard.title')}</h1>
          <p className="text-xs sm:text-sm text-slate-500">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex gap-1 rounded-xl p-1" style={{ background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)' }}>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                days === d ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <MetricCard
          title={t('dashboard.views')}
          value={overview?.totalViews || 0}
          icon={<Eye size={18} />}
          color="#6366f1"
        />
        <MetricCard
          title={t('dashboard.likes')}
          value={overview?.totalLikes || 0}
          icon={<Heart size={18} />}
          color="#ec4899"
        />
        <MetricCard
          title={t('dashboard.comments')}
          value={overview?.totalComments || 0}
          icon={<MessageSquare size={18} />}
          color="#f59e0b"
        />
        <MetricCard
          title={t('dashboard.shares')}
          value={overview?.totalShares || 0}
          icon={<Share2 size={18} />}
          color="#10b981"
        />
        <MetricCard
          title={t('dashboard.followers')}
          value={overview?.followerCount || 0}
          icon={<Users size={18} />}
          color="#8b5cf6"
        />
      </div>

      {/* Metric Selector + Chart */}
      <div>
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {METRICS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setSelectedMetric(m)}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-all ${
                selectedMetric === m
                  ? 'bg-indigo-500 text-white'
                  : 'bg-white/60 text-slate-600 hover:bg-white'
              }`}
            >
              {m.charAt(0) + m.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        {performance?.data && (
          <PerformanceChart data={performance.data} metric={selectedMetric} />
        )}
      </div>

      {/* Bottom Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {audience && (
          <FollowerGrowth
            totalFollowers={audience.totalFollowers}
            recentFollowers={audience.recentFollowers}
          />
        )}
      </div>
    </motion.div>
  );
}
